import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollTo } from "@/components/Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1610374634235-b51ef357f905?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const LINES = [
  { text: "UNLEASH THE", cls: "text-neutral-100" },
  { text: "TRUE POWER", cls: "text-outline" },
  { text: "IN YOUR ENGINE", cls: "text-neutral-100", accent: "ENGINE" },
];

const STATS = [
  { value: "+35%", label: "Avg BHP Gain" },
  { value: "+40%", label: "Torque Increase" },
  { value: "100%", label: "Custom Software" },
  { value: "1 Year", label: "Map Warranty" },
];

const TachArc = () => (
  <svg viewBox="0 0 220 130" className="w-44 h-28 sm:w-56 sm:h-32">
    <defs>
      <linearGradient id="tachGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#4B5563" />
        <stop offset="55%" stopColor="#FF2E4D" />
        <stop offset="100%" stopColor="#FF2E4D" />
      </linearGradient>
    </defs>
    {Array.from({ length: 13 }).map((_, i) => {
      const angle = Math.PI + (i * Math.PI) / 12;
      const x1 = 110 + 78 * Math.cos(angle);
      const y1 = 115 + 78 * Math.sin(angle);
      const x2 = 110 + (i > 8 ? 66 : 70) * Math.cos(angle);
      const y2 = 115 + (i > 8 ? 66 : 70) * Math.sin(angle);
      return (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={i > 8 ? "#FF2E4D" : "#4B5563"}
          strokeWidth={i % 3 === 0 ? 3 : 1.5}
        />
      );
    })}
    <motion.path
      d="M 32 115 A 78 78 0 0 1 188 115"
      fill="none"
      stroke="url(#tachGrad)"
      strokeWidth="5"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 2, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
    />
    <motion.line
      x1="110"
      y1="115"
      x2="110"
      y2="52"
      stroke="#F9FAFB"
      strokeWidth="3"
      strokeLinecap="round"
      initial={{ rotate: -90 }}
      animate={{ rotate: 62 }}
      transition={{ duration: 2, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformOrigin: "110px 115px" }}
    />
    <circle cx="110" cy="115" r="7" fill="#FF2E4D" />
  </svg>
);

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden carbon-grid">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Sports car at night"
          className="w-full h-[120%] object-cover object-center opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-asphalt/80 via-asphalt/40 to-asphalt" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(255,46,77,0.14),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-16"
      >
        <div className="flex items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-nitro mb-6 flex items-center gap-3"
            >
              <span className="inline-block w-10 h-px bg-nitro" />
              Precision ECU Remapping — Est. Performance
            </motion.p>

            <h1 className="font-heading font-extrabold uppercase tracking-tight leading-[0.92] text-5xl sm:text-7xl lg:text-8xl">
              {LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                    className={`block ${line.cls}`}
                  >
                    {line.accent ? (
                      <>
                        IN YOUR <span className="text-nitro">ENGINE</span>
                      </>
                    ) : (
                      line.text
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-neutral-400 leading-relaxed"
            >
              Custom ECU remapping, Stage 1–3 tuning and dyno-proven power gains.
              Select your vehicle below and see exactly what your engine is capable of.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Button
                data-testid="hero-book-cta"
                onClick={() => scrollTo("#vehicle-selector")}
                className="bg-nitro hover:bg-[#ff4561] text-white font-heading font-semibold uppercase tracking-widest text-lg rounded-full px-9 py-7 animate-pulse-glow transition-transform duration-300 hover:scale-[1.03]"
              >
                <Gauge className="mr-2" size={20} />
                Check Your Gains
              </Button>
              <Button
                data-testid="hero-secondary-cta"
                onClick={onBook}
                variant="outline"
                className="border-[#262933] bg-transparent hover:bg-panel hover:border-nitro text-neutral-200 font-heading font-semibold uppercase tracking-widest text-lg rounded-full px-9 py-7 transition-colors duration-300"
              >
                Book a Remap
              </Button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="hidden xl:block shrink-0"
          >
            <TachArc />
            <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 text-center -mt-3 uppercase">
              Redline unlocked
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-[#1F232D]"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className="py-6 pr-6 border-r border-[#1F232D] last:border-r-0 md:[&:nth-child(2)]:border-r"
            >
              <p className="font-heading font-bold text-3xl sm:text-4xl text-neutral-100">
                {s.value}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-500 mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#vehicle-selector")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-500 hover:text-nitro transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
};
